// Phase 2D.1C checkpoint 3 — dependency-free helpers for the DESTRUCTIVE
// lifecycle.
//
// Nothing here authorizes anything. A purge is re-validated server-side at the
// moment it executes: the lifecycle state, the elapsed deadline, the retyped
// name and the absence of a running or completed job are all checked again by the
// database. A preview fetched by a browser is informational only, and a stale one
// can never be replayed as permission.
//
// It is deliberately import-free so the exact-name comparison can be executed as
// is, in node, by the offline test suite.

/** Mirrors ``PURGE_GRACE_DAYS`` in backend/quowada/platform_admin.py. */
export const PURGE_GRACE_DAYS = 7;

/** Every table the database purges, in the order the console lists them. */
export const PURGE_TABLES = [
  "quotations",
  "quotation_files",
  "quotation_public_links",
  "quotation_events",
  "quotation_responses",
  "quotation_emails",
  "quotation_email_events",
  "customers",
  "internal_notifications",
  "audit_logs",
  "organization_members",
  "organization_member_invitations",
  "organization_teams",
  "organization_team_members",
  "organization_owner_invitations",
  "organization_role_entitlements",
  "organization_licenses",
  "organization_platform_metadata",
  "platform_audit_logs",
];

// Sanitized codes the purge ledger may store. Anything unknown falls back to the
// generic line, so a new code can never render an English internal label.
const PURGE_ERROR_KEYS = {
  storage_delete_failed: "platform.purge_error_storage",
  storage_not_deleted: "platform.purge_error_storage",
  grace_period_active: "platform.purge_error_grace",
  organization_name_mismatch: "platform.purge_error_name",
  organization_not_deletion_pending: "platform.purge_error_state",
  purge_job_not_found: "platform.purge_error_job",
  purge_job_not_running: "platform.purge_error_job",
  purge_failed: "platform.purge_error_failed",
};

export function purgeErrorKey(code) {
  return PURGE_ERROR_KEYS[code] || "platform.purge_error_failed";
}

/**
 * Does the retyped value match the organization name EXACTLY?
 *
 * Only the surrounding whitespace of what the human typed is forgiven — exactly
 * what the backend forgives before the database compares the rest character for
 * character. A near miss can never destroy an organization.
 */
export function matchesExactName(typed, actual) {
  if (typeof typed !== "string" || typeof actual !== "string") return false;
  const target = actual.trim();
  if (!target) return false;
  return typed.trim() === target;
}

/**
 * Per-table row counts for the dry run: affected tables only, stable order.
 *
 * Counts are aggregates. No storage path, file name, customer, quotation title
 * or e-mail address is part of this projection — the backend does not send them.
 */
export function purgeImpactRows(rowCounts) {
  const counts = rowCounts && typeof rowCounts === "object" ? rowCounts : {};
  const affected = (table) => Number(counts[table]) > 0;
  const row = (table) => ({ table, count: Number(counts[table]) });
  const known = PURGE_TABLES.filter(affected).map(row);
  const unknown = Object.keys(counts)
    .filter((table) => !PURGE_TABLES.includes(table) && affected(table))
    .sort()
    .map(row);
  return [...known, ...unknown];
}
